import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  UserIcon, 
  MapPinIcon, 
  CreditCardIcon, 
  ShoppingBagIcon, 

  BellIcon,
  ShieldCheckIcon,
  StarIcon,
  PencilIcon,

  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  TruckIcon,
  ClockIcon,
  CameraIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import AddressManager from '../components/AddressManager';
import OrderDetailModal from '../components/OrderDetailModal';
import '../styles/animations.css';



interface Order {
  id: number;
  order_number: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  payment_status: string;
  total_amount: number;
  created_at: string;
  order_items: Array<{
    id: number;
    product_name: string;
    variant_color: string;
    variant_size: string;
    quantity: number;
    price: number;
    total: number;
    product?: {
      productImages?: Array<{ image_url: string }>;
    };
  }>;
  shippingAddress?: {
    formatted_address: string;
  };
  townshipDelivery?: {
    estimated_days: number;
  };
}

const Profile: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  
  // Get active tab from URL hash or default to overview
  const getActiveTabFromUrl = useCallback(() => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['overview', 'addresses', 'orders', 'notifications', 'security'];
    return validTabs.includes(hash) ? hash : 'overview';
  }, [location.hash]);
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromUrl());
  
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Order detail modal state
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: ''
  });

  // Date formatting utilities
  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return 'Not provided';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatInputDate = useCallback((dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
    } catch (error) {
      return '';
    }
  }, []);

  const formatGender = (gender: string) => {
    if (!gender) return 'Not provided';
    switch (gender.toLowerCase()) {
      case 'male': return 'Male';
      case 'female': return 'Female';
      case 'other': return 'Other';
      case 'prefer_not_to_say': return 'Prefer not to say';
      default: return gender;
    }
  };

  const fetchUserData = useCallback(async () => {
    try {
      const response = await apiService.getProfile();
      
      setUser(response.user);
      setEditForm({
        name: response.user.name || '',
        email: response.user.email || '',
        phone: response.user.phone || '',
        date_of_birth: formatInputDate(response.user.date_of_birth),
        gender: response.user.gender || ''
      });
      
      // Fetch recent orders
      try {
        const recentOrders = await apiService.getRecentOrders();
        setOrders(recentOrders);
      } catch (error) {
        setOrders([]);
      }
      
    } catch (error) {
      // Failed to fetch user data
    } finally {
      setIsLoading(false);
    }
  }, [formatInputDate]);

  // Update active tab when URL hash changes
  useEffect(() => {
    const newActiveTab = getActiveTabFromUrl();
    if (newActiveTab !== activeTab) {
      setActiveTab(newActiveTab);
    }
  }, [getActiveTabFromUrl, activeTab]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleUpdateProfile = async () => {
    try {
      await apiService.updateCustomerProfile(editForm);
      setUser({ ...user, ...editForm });
      setIsEditing(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
    } catch (error) {
      // Handle error (show error message)
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB.');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload avatar
    try {
      setIsUploadingAvatar(true);
      const response = await apiService.uploadAvatar(file);
      
      // Update user data with new avatar URL
      const updatedUser = { ...user, avatar_url: response.avatar_url };
      setUser(updatedUser);
      
      // Update localStorage to persist the avatar across the application
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Dispatch custom event to notify other components of user update
      window.dispatchEvent(new CustomEvent('userUpdated', { detail: updatedUser }));
      
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
    } catch (error) {
      alert('Failed to upload avatar. Please try again.');
      setAvatarPreview(null);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'text-green-600 bg-green-50';
      case 'shipped': return 'text-blue-600 bg-blue-50';
      case 'processing': return 'text-yellow-600 bg-yellow-50';
      case 'pending': return 'text-gray-600 bg-gray-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered': return <CheckCircleIcon className="w-4 h-4" />;
      case 'shipped': return <TruckIcon className="w-4 h-4" />;
      case 'processing': return <ClockIcon className="w-4 h-4" />;
      case 'pending': return <ClockIcon className="w-4 h-4" />;
      case 'cancelled': return <XCircleIcon className="w-4 h-4" />;
      default: return <ClockIcon className="w-4 h-4" />;
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US').format(price) + ' Ks';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Utility function to construct proper avatar URL
  const getAvatarUrl = (avatarUrl: string | null | undefined): string | undefined => {
    if (!avatarUrl) return undefined;
    if (avatarUrl.startsWith('http')) return avatarUrl;
    return `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://139.59.98.59'}/storage/${avatarUrl}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: UserIcon },
    { id: 'orders', name: 'My Orders', icon: ShoppingBagIcon },
    { id: 'addresses', name: 'Addresses', icon: MapPinIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-[72px] pb-8">
      {/* Modern Success Notification - Matching Website Theme */}
      {showSuccessMessage && (
        <div className="fixed top-6 right-6 z-50 animate-in transform transition-all duration-500 ease-out">
          <div className="bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 p-6 max-w-sm backdrop-blur-sm">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/20 ring-offset-2 ring-offset-gray-900">
                  <CheckCircleIcon className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-white">Profile Updated!</h3>
                  <button
                    onClick={() => setShowSuccessMessage(false)}
                    className="text-gray-400 hover:text-white transition-colors duration-200 p-1 hover:bg-white/10 rounded-full"
                  >
                    <XCircleIcon className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-300 mt-1 leading-relaxed">
                  Your profile information has been successfully updated and saved.
                </p>
                <div className="mt-4 flex items-center text-xs font-medium text-blue-200 bg-gradient-to-r from-blue-500/20 to-purple-500/20 px-3 py-2 rounded-lg border border-blue-500/30">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mr-2 animate-pulse"></div>
                  <span>Changes saved automatically</span>
                  <CheckCircleIcon className="w-3 h-3 ml-2 text-blue-400" />
                </div>
              </div>
            </div>
            {/* Animated Progress Bar */}
            <div className="mt-5 w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full animate-progress shadow-sm"></div>
            </div>
            {/* Subtle glow effect */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 pointer-events-none"></div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl shadow-sm mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 px-4 sm:px-8 py-6 sm:py-12">
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="relative group">
                <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full overflow-hidden shadow-2xl ring-2 sm:ring-4 ring-white relative">
                  {user?.avatar_url || avatarPreview ? (
                    <img
                      src={avatarPreview || getAvatarUrl(user.avatar_url)}
                      alt={user?.name || 'Profile'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Avatar image failed to load
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center">
                      <span className="text-white text-xl sm:text-3xl font-bold tracking-wide">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  
                  {/* Upload overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center cursor-pointer" onClick={handleAvatarClick}>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {isUploadingAvatar ? (
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-6 sm:w-6 border-b-2 border-white"></div>
                      ) : (
                        <CameraIcon className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Status indicator */}
                <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-6 sm:h-6 bg-green-400 rounded-full border-2 sm:border-4 border-white"></div>
                
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </div>
              <div className="text-white flex-1 min-w-0">
                <h1 className="text-lg sm:text-3xl font-bold mb-1 sm:mb-2 truncate">{user?.name}</h1>
                <p className="text-gray-300 mb-1 text-xs sm:text-base truncate">{user?.email}</p>
                <div className="flex items-center space-x-2 sm:space-x-4 text-xs sm:text-sm text-gray-300">
                  {user?.is_vip ? (
                    <span className="flex items-center">
                      <StarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-yellow-400" />
                      <span className="hidden xs:inline">VIP </span>Member
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <StarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-gray-400" />
                      <span className="hidden xs:inline">Regular </span>Member
                    </span>
                  )}
                  <span className="hidden sm:inline">Member since {user?.member_since || 'January 2024'}</span>
                  <span className="sm:hidden">Since {user?.member_since?.split(' ')[1] || '2024'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-1 sm:p-2">
              <nav className="space-y-0.5 sm:space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        navigate(`/profile#${tab.id}`);
                      }}
                      className={`w-full flex items-center px-3 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm font-medium rounded-xl transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'bg-gray-900 text-white shadow-lg'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="p-4 sm:p-8">
                  <div className="flex justify-between items-center mb-4 sm:mb-8">
                    <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Account Overview</h2>
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-xs sm:text-sm"
                    >
                      <PencilIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-600 text-xs sm:text-sm font-medium">Total Orders</p>
                          <p className="text-lg sm:text-2xl font-bold text-blue-900">{orders.length}</p>
                        </div>
                        <ShoppingBagIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-600 text-xs sm:text-sm font-medium">Total Spent</p>
                          <p className="text-lg sm:text-2xl font-bold text-green-900">{formatPrice(user?.total_spent || 0)}</p>
                        </div>
                        <CreditCardIcon className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
                      </div>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="border-t pt-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Personal Information</h3>
                    {isEditing ? (
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                            <input
                              type="text"
                              value={editForm.name}
                              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                            <input
                              type="email"
                              value={editForm.email}
                              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                            <input
                              type="tel"
                              value={editForm.phone}
                              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                            <input
                              type="date"
                              value={editForm.date_of_birth}
                              onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                            <select
                              value={editForm.gender}
                              onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            >
                              <option value="">Select Gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="other">Other</option>
                              <option value="prefer_not_to_say">Prefer not to say</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex space-x-4">
                          <button
                            onClick={handleUpdateProfile}
                            className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                          >
                            Save Changes
                          </button>
                          <button
                            onClick={() => setIsEditing(false)}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                          <p className="text-lg text-gray-900">{user?.name || 'Not provided'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
                          <p className="text-lg text-gray-900">{user?.email}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Phone Number</label>
                          <p className="text-lg text-gray-900">{user?.phone || 'Not provided'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Date of Birth</label>
                          <p className="text-lg text-gray-900">{formatDisplayDate(user?.date_of_birth)}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Gender</label>
                          <p className="text-lg text-gray-900">{formatGender(user?.gender)}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Member Since</label>
                          <p className="text-lg text-gray-900">
                            {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long'
                            }) : 'January 2024'}
                          </p>
                        </div>
                        {user?.is_vip && (
                          <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">VIP Since</label>
                            <p className="text-lg text-gray-900">
                              {user?.vip_since ? new Date(user.vip_since).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }) : 'N/A'}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <div className="p-4 sm:p-8">
                  <div className="flex justify-between items-center mb-4 sm:mb-8">
                    <h2 className="text-lg sm:text-2xl font-bold text-gray-900">My Orders</h2>
                    <p className="text-gray-600 text-xs sm:text-base">Last 5</p>
                  </div>
                  
                  {orders.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                        <ShoppingBagIcon className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No Orders Yet</h3>
                      <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base px-4">You haven't placed any orders yet. Start shopping to see your orders here.</p>
                      <button
                        onClick={() => navigate('/product-signature')}
                        className="px-4 py-2 sm:px-6 sm:py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm sm:text-base"
                      >
                        Start Shopping
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {orders.map((order) => (
                        <div key={order.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">Order #{order.order_number}</h3>
                              <p className="text-gray-500">{formatDate(order.created_at)} • {order.order_items?.length || 0} items</p>
                              <div className="mt-2 flex items-center space-x-3">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  order.payment_status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                                  order.payment_status === 'approved' ? 'bg-green-100 text-green-800' :
                                  order.payment_status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {order.payment_status === 'pending_approval' ? 'Payment Under Review' :
                                 order.payment_status === 'approved' ? 'Payment Approved' :
                                 order.payment_status === 'rejected' ? 'Payment Rejected' :
                                 order.payment_status}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-gray-900">{formatPrice(order.total_amount)}</p>
                            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                              {getStatusIcon(order.status)}
                              <span className="ml-1 capitalize">{order.status}</span>
                            </div>
                          </div>
                        </div>
                        {order.townshipDelivery?.estimated_days && (
                          <div className="flex items-center text-sm text-gray-600 mb-4">
                            <TruckIcon className="w-4 h-4 mr-2" />
                            Estimated delivery: {order.townshipDelivery.estimated_days} days
                          </div>
                        )}
                        <div className="flex space-x-3">
                          <button 
                            onClick={() => setSelectedOrderNumber(order.order_number)}
                            className="flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                          >
                            <EyeIcon className="w-4 h-4 mr-2" />
                            View Details
                          </button>
                        </div>
                      </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Addresses Tab */}
              {activeTab === 'addresses' && user && <AddressManager userName={user.name} showDeliveryInfo={false} />}

              {/* Other tabs placeholder */}
              {!['overview', 'orders', 'addresses'].includes(activeTab) && (
                <div className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {tabs.find(tab => tab.id === activeTab)?.name}
                  </h2>
                  <div className="bg-gray-50 rounded-xl p-12 text-center">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      {React.createElement(tabs.find(tab => tab.id === activeTab)?.icon || UserIcon, {
                        className: "w-8 h-8 text-gray-400"
                      })}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Coming Soon</h3>
                    <p className="text-gray-600">This section is under development.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrderNumber && (
        <OrderDetailModal
          orderNumber={selectedOrderNumber}
          onClose={() => setSelectedOrderNumber(null)}
        />
      )}
    </div>
  );
};

export default Profile;
